import { useReactiveVar } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Divider from '@material-ui/core/Divider'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import React, { useEffect } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { LODS, STATUS } from 'shared/utils/dropdownUtils'
import { useProjectPropertiesMutation } from '../api/useProjectPropertiesMutation'
import { useProjectPropertiesQuery } from '../api/useProjectPropertiesQuery'
import { useProjectPropertiesHandler } from '../hooks/useProjectPropertiesHandler'
import { useProjectPropertiesStore } from '../ProjectPropertiesStore'
import ProjectPropertiesAutoCompleteWithLabel from './ProjectPropertiesAutoCompleteWithLabel'

const DEFAULT_LOS_LEVEL = '100'
interface styleProps {
    isExtendedView?: boolean | undefined
}
const elementPropertiesStyles = makeStyles((theme: Theme) => ({
    container: {
        width: '100%',
    },
    justifyCenter: {
        justifyContent: 'center',
    },
    elementPropertiesCont: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        display: 'inline-flex',
        alignItems: 'center',
    },
    propertiesLabel: {
        color: '#314959',
        lineHeight: pxToRem(15.23),
        fontSize: pxToRem(13),
        fontFamily: 'Roboto',
        fontWeight: 700,
        marginRight: pxToRem(12),
    },
    divider: {
        background: '#314959',
        alignSelf: 'center',
        height: pxToRem(12),
    },
    smallWidth: { width: pxToRem(60) },
    projectPropertyInnerWrapper: {
        padding: ({ isExtendedView }: styleProps) =>
            isExtendedView ? theme.spacing(0, 0, 5, 0) : theme.spacing(0, 3),
    },
}))
export interface ProjectPropertiesInterface {
    projectElementNode?: ProjectElementNode
    projectId: string
    isLoading?: boolean
}

const ProjectProperties = ({
    projectElementNode,
    projectId,
    isLoading = false,
}: ProjectPropertiesInterface) => {
    const { previewFeatures } = usePreviewFeatures()
    const classes = elementPropertiesStyles({
        isExtendedView: previewFeatures.hasExtendedView,
    })
    const assignedUsersList = useProjectPropertiesStore(
        (s) => s.assignedUsersList
    )
    const contentTeamFromStudioPage = isReactiveContentTeamFromStudioPage()
        ?.value
    const queryApi = useProjectPropertiesQuery({
        projectId,
        contentTeamFromStudioPage,
    })
    const mutationApi = useProjectPropertiesMutation({
        projectId,
        contentTeamFromStudioPage,
    })
    const fetchAssignedUsersList = queryApi.fetchAssignedUserList
    const updateProjectProperties = mutationApi.updateProjectProperties
    const { projectMetadata: projectElementMetadata } = useReactiveVar(
        workspaceSelectedElement
    )

    const action = useProjectPropertiesHandler(updateProjectProperties)
    useEffect(() => {
        action.initializeSelectedValues()
    }, [assignedUsersList, projectElementNode, projectElementMetadata])

    useEffect(() => {
        fetchAssignedUsersList()
    }, [])

    return (
        <div
            className={clsx(
                classes.container,
                'ProjectPropertiesClass',
                'header'
            )}
        >
            <GridContainer
                item
                wrap="nowrap"
                className={clsx(
                    classes.justifyCenter,
                    classes.projectPropertyInnerWrapper
                )}
            >
                <ProjectPropertiesAutoCompleteWithLabel
                    isDisabled={
                        !Boolean(projectElementNode?.elementId) || isLoading
                    }
                    placeholderText={'Select State'}
                    dropdownList={STATUS.map((item) => item.name)}
                    parameterName={'state'}
                    dataTestId="stateTestId"
                    selectedValue={
                        STATUS.filter(
                            (state) =>
                                state.value ===
                                action.currentSelectedValues.state
                        )[0].name
                    }
                    handleChange={action.handleChange}
                    label={'State:'}
                />
                <Divider orientation="vertical" className={classes.divider} />
                <ProjectPropertiesAutoCompleteWithLabel
                    isDisabled={
                        !Boolean(projectElementNode?.elementId) || isLoading
                    }
                    placeholderText={'Select Team Member'}
                    dropdownList={assignedUsersList.map(
                        (users) => users.userName
                    )}
                    parameterName={'assignedUserId'}
                    dataTestId="assignedToTestId"
                    selectedValue={
                        assignedUsersList.find((item) =>
                            action.currentSelectedValues.assignedUserId
                                ? item.userId ===
                                  action.currentSelectedValues.assignedUserId
                                : !item.userId
                        )?.userName ?? null
                    }
                    handleChange={action.handleChange}
                    label={'Assigned To:'}
                />
                <Divider orientation="vertical" className={classes.divider} />
                <ProjectPropertiesAutoCompleteWithLabel
                    isDisabled={
                        !Boolean(projectElementNode?.elementId) || isLoading
                    }
                    placeholderText={'Assign Current LOS'}
                    dropdownList={LODS}
                    parameterName={'currentLOD'}
                    dataTestId="currentLODTestId"
                    selectedValue={
                        action.currentSelectedValues.currentLOD?.toString() ??
                        DEFAULT_LOS_LEVEL
                    }
                    handleChange={action.handleChange}
                    customizedStyle={classes.smallWidth}
                    label={'Current LOS:'}
                />
                {!contentTeamFromStudioPage && (
                    <>
                        <Divider
                            orientation="vertical"
                            className={classes.divider}
                        />
                        <ProjectPropertiesAutoCompleteWithLabel
                            isDisabled={
                                !Boolean(projectElementNode?.elementId) ||
                                isLoading
                            }
                            placeholderText="Assign Target LOS"
                            dropdownList={LODS}
                            parameterName={'targetLOD'}
                            dataTestId="targetLODTestId"
                            selectedValue={
                                action.currentSelectedValues.targetLOD?.toString() ??
                                DEFAULT_LOS_LEVEL
                            }
                            handleChange={action.handleChange}
                            customizedStyle={classes.smallWidth}
                            label={'Target LOS:'}
                        />
                    </>
                )}
            </GridContainer>
        </div>
    )
}

export default ProjectProperties
